/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import googleMaps from '../util/google-maps.js';
import _ from "underscore";
import util from '../util/util.js';
import SortableTableViewModel from './shared/sortable-table.js';
import * as Chartist from 'chartist';
import Squire from "squire-rte";
import setupEditorUI from "../util/squire-ui.js";

import "./survey-summary.js";
import "./survey-summary-header.js";
import '../util/util.js';

var SharedSiteDetailsViewModel = function() {
	document.body.classList.add("birdata-fullscreen");

	var self = this;

	self.showEditor = ko.observable(false);

	self.loaded = ko.observable(false);

	self.map = ko.observable();
	self.mapOptions = ko.observable();

	self.site = ko.observable();
	self.surveys = ko.observable();
	self.species = ko.observable();

	self.status = ko.observable();
	self.saveInProgress = ko.observable(false);
	self.canEdit = ko.pureComputed(function() {
		return self.site() != null && self.site().can_edit;
	});

	self.selectedSurvey = ko.observable();

	self.showEditButton = ko.pureComputed(function() {
		return self.canEdit() && !self.saveInProgress();
	});

	self.clickRecordSurvey = function() {
		window.location = "/record-survey#shared_site_id=" + self.site().id;
	};

	var editor;

	self.saveSiteDescription = function() {
		var description = editor.getHTML();
		var site = self.site();

		self.showEditor(false);
		self.status('Saving changes...');

		self.saveInProgress(true);
		birdata.updateSharedSite({
			id: self.site().id,
			description: description
		}).then(function() {
			self.status('');
			self.saveInProgress(false);
			// Update site locally
			self.site(_.defaults({
				description: description
			}, site));
		}).catch(function() {
			self.status("Failed to save changes");
			self.saveInProgress(false);
		});
	};

	self.cancelEditSiteDescription = function() {
		self.showEditor(false);
	};

	self.editSiteDescription = function() {
		var description = self.site().description || '';
		self.showEditor(true);

		setTimeout(function() {
			var iframe = document.querySelector(".text-editor > iframe");
			editor = new Squire(iframe.contentWindow.document);
			editor.setHTML(description);
			editor.addStyles(
				'img {' +
				'   max-width: 100%' +
				'}' +
				'body {' +
				'   font-family: Helvetica, sans-serif;' +
				'}'
				);

			setupEditorUI(document.querySelector(".toolbar"), editor);
		}, 100);
	};

	function surveyYear(survey) {
		return survey.start_date ? +survey.start_date.substr(0, 4) : 0;
	}

	self.stats = ko.pureComputed(function() {
		var surveys = self.surveys();

		if(surveys) {
			var stats = {
				surveyCount: surveys.length,
				observerCount: _.chain(surveys).pluck('user_id').uniq().value().length,
				yearCount: _.chain(surveys).map(surveyYear).uniq().without(0).value().length
			};

			if(stats.yearCount == 0) {
				stats.surveysPerYear = 0;
			} else {
				stats.surveysPerYear = (stats.surveyCount / stats.yearCount).toFixed(2);
			}

			return stats;
		} else {
			return false;
		}
	});

	self.speciesImageURL = function(species) {
		return birdata.speciesImageURL(species.id, 'small_square');
	};

	function surveyTypeString(code) {
		if(self.lookup) {
			var type = self.lookup.find('survey_type', code);
			return type ? type.name : code;
		}
	}

	self.clickSurvey = function(survey) {
		self.selectedSurvey(survey);
		birdata.survey(survey.id).then(function(fullSurvey) {
			if(self.selectedSurvey() == survey) {
				self.selectedSurvey(fullSurvey);
			}
		});
	};

	self.closeSurvey = function() {
		self.selectedSurvey(null);
	};

	self.siteSurveyType = ko.pureComputed(() => {
		if(self.site()) {
			return '<b>Survey type: </b>' + surveyTypeString(self.site().survey_type);
		}
	});

	self.table = new SortableTableViewModel([
		{
			name: ko.pureComputed(function() { return 'Surveys (' + self.surveys().length + ')'; }),
			value: function(survey) { var name = survey.survey_point.name; return name ? name.trim().toLowerCase() : ""; },
			formatted: function(survey) { return survey.survey_point.name || "(Unnamed)"; },
			id: 'survey'
		}
		, {
			name: 'Date',
			value: function(survey) { return survey.start_date || ''; },
			formatted: function(survey) { return this.value(survey); },
			id: 'date'
		}, {
			name: 'Time',
			value: function(survey) { return survey.start_time || ''; },
			formatted: function(survey) { return this.value(survey); },
			id: 'time'
		}, {
			name: 'Duration',
			value: function(survey) { return survey.duration_in_minutes; },
			formatted: function(survey) { return util.formatDuration(survey.duration_in_minutes); },
			id: 'duration'
		}, {
			name: 'Type',
			value: function(survey) { return surveyTypeString(survey.survey_type); },
			formatted: function(survey) { return this.value(survey); },
			id: 'type'
		}, {
			name: 'Species sighted',
			value: function(survey) { return survey.species_count; },
			formatted: function(survey) { return survey.species_count; },
			id: 'species'
		}
	]);

	self.table.sortDescriptors([
		{ column: 'time', ascending: false },
		{ column: 'date', ascending: false }
	]);

	self.sortedSurveys = ko.pureComputed(function() {
		var surveys = self.surveys().slice(0); // shallow clone array

		console.log(self.table.sortDescriptors());

		_.each(self.table.sortDescriptors(), function(descriptor) {
			if(!descriptor.ascending) surveys.reverse();
			var column = self.table.column(descriptor.column);
			surveys = _.sortBy(surveys, column.params.value);
			if(!descriptor.ascending) surveys.reverse();
		});

		return surveys;
	});

	ko.computed(function() {
		var surveys = self.surveys();
		if(surveys && surveys.length > 0) {
			var years = _.chain(surveys).map(surveyYear).without(0).value();
			var minYear = _.min(years);
			var maxYear = _.max(years);
			var labels = _.range(minYear, maxYear + 1);

			var valueMap = _.countBy(surveys, surveyYear);

			var chartData = {
				series: [ labels.map(function(year) { return {x: year, y: valueMap[year] || 0}; }) ]
			};

			var options = {
				axisY: {
					onlyInteger: true
				},
				axisX: {
					type: Chartist.AutoScaleAxis,
					scaleMinSpace: 30,
					onlyInteger: true
				},
				lineSmooth: Chartist.Interpolation.monotoneCubic(),
				showLine: true,
				showPoint: false
			};

			setTimeout(function() {
				new Chartist.LineChart('.surveys-vs-year', chartData, options);
			});
		}
	});

	function init() {
		var params = util.queryParams();
		var siteId = params.id;

		if(!siteId) {
			window.location = '/shared-sites';
			return;
		}

		Promise.all([
			birdata.sharedSite(siteId),
			birdata.lookup(),
			birdata.surveysAtSharedSite(siteId),
			birdata.me(true).noLoginRedirect().catch(function() { return {}; })
		]).then(function([site, lookup, surveys, me]) {
				self.lookup = lookup;
				self.site(site);
				self.surveys(surveys);
				self.loaded(true);
			});

		Promise.all([
			birdata.sharedSiteSpecies(siteId),
			birdata.speciesList({ 'supertaxa': 'all' })
		]).then(function([speciesData, speciesList]) {
				self.species(_.chain(speciesList)
					.filter(function(sp) {
						return !!speciesData[sp.id];
					})
					.map(function(sp) {
						var siteData = speciesData[sp.id];
						siteData.reportingRate = ko.pureComputed(function() {
							if(self.surveys() && self.surveys().length) {
								return (siteData.times_sighted * 100 / self.surveys().length).toFixed(2);
							} else {
								return null;
							}
						});

						return {
							species: sp,
							siteData: siteData
						};
					})
					.sortBy(function(s) {
						return s.species.sort;
					})
					.value());
			});

		document.addEventListener('keydown', function(e) {
			if(e.keyCode == 27) { // ESCAPE
				self.selectedSurvey(null);
			}
		});
	}

	init();

	ko.computed(function() {
		var map = self.map();
		var site = self.site();
		if(map && site) setupMap(map, site);
	});

	function setupMap(map, site) {
		map.fitBounds(new google.maps.LatLngBounds(
			new google.maps.LatLng(site.bounds.miny, site.bounds.minx),
			new google.maps.LatLng(site.bounds.maxy, site.bounds.maxx)));

		googleMaps.addFeatureToMap(site.geom, map);
	}

	googleMaps.load().then(function() {
		self.mapOptions(googleMaps.defaultOptions({
			center: new google.maps.LatLng(-22.50831, 136.078612),
			zoom: 4,
			mapTypeId: google.maps.MapTypeId.HYBRID
		}));
	});
};

ko.components.register('shared-site-details', {
	viewModel: function() {
		return new SharedSiteDetailsViewModel();
	},
	template: require('../../templates/shared-site-details.html')
});

export default SharedSiteDetailsViewModel;
